"use strict";
/**
 * The rules of an App as data and their check (app-rules.ts holds the
 * schemas of the manifest). This module imports nothing at run time, so the
 * App runtime checks rules without the schemas.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_RULES_LINKED_MAX = exports.AppRuleViolatedError = void 0;
exports.appRuleSourceKeys = appRuleSourceKeys;
exports.appRuleRowChange = appRuleRowChange;
exports.appRuleColumnIds = appRuleColumnIds;
exports.evaluateAppRules = evaluateAppRules;
/**
 * A write the rules of the App refuse: nothing of it is written, and the
 * caller learns each broken rule at its row. Hosts answer it with the code
 * `APP_RULE_VIOLATED`, the words of APP_RULE_VIOLATED_MESSAGE
 * (app-base-refusals.ts) and `violations`.
 */
class AppRuleViolatedError extends Error {
    /**
     * `locale`: the default locale of the App, once the host knows it; the
     * answer speaks it (appRuleViolatedMessage).
     */
    constructor(violations, locale) {
        super('APP_RULE_VIOLATED');
        this.violations = violations;
        this.locale = locale;
        this.code = 'APP_RULE_VIOLATED';
        this.name = 'AppRuleViolatedError';
    }
}
exports.AppRuleViolatedError = AppRuleViolatedError;
/** The sources a rule reads: its own and, for `linked`, the linked one. */
function appRuleSourceKeys(rule) {
    return rule.kind === 'linked'
        ? [rule.sourceKey, rule.targetSourceKey]
        : [rule.sourceKey];
}
/**
 * One row of a write as the rules see it. `stored` reads a cell of the stored
 * row (null for a new row), `values` are the cells the write sets (null
 * deletes). A new row names every column of the rules, the unwritten ones
 * empty; a change lays its cells over the stored row.
 */
function appRuleRowChange(ruleSet, sourceKey, rowId, stored, values) {
    const columnIds = appRuleColumnIds(ruleSet, sourceKey);
    const before = stored
        ? Object.fromEntries(columnIds.map(id => [id, stored(id)]))
        : null;
    const after = values === null
        ? null
        : {
            ...(before ??
                Object.fromEntries(columnIds.map(id => [id, undefined]))),
            ...values,
        };
    return { rowId, before, after };
}
/** The columns the rules read on rows of one source, filters included. */
function appRuleColumnIds(ruleSet, sourceKey) {
    const columns = new Set();
    const visit = (filter) => {
        for (const node of filter?.conditions ?? [])
            if (node.type === 'group')
                visit(node);
            else
                columns.add(node.left.name);
    };
    for (const rule of ruleSet.rules) {
        if (rule.kind === 'linked' && rule.targetSourceKey === sourceKey)
            visit(rule.filter);
        if (rule.sourceKey !== sourceKey)
            continue;
        if (rule.kind === 'unique')
            rule.columnIds.forEach(id => columns.add(id));
        else if (rule.kind === 'groupCount' || rule.kind === 'groupSum') {
            rule.groupBy.forEach(id => columns.add(id));
            if (rule.kind === 'groupSum')
                columns.add(rule.columnId);
            visit(rule.where);
        }
        else
            columns.add(rule.columnId);
        if ('when' in rule)
            visit(rule.when);
    }
    return [...columns];
}
/** The most stored rows one `linked` rule re-checks for one write. */
exports.APP_RULES_LINKED_MAX = 500;
/**
 * The rules one write breaks. A rule of one column judges a value the write
 * adds or changes (an own property of `after`, also when its value is empty):
 * a page that adds a row names every column of its rules, one that changes a
 * row the columns it changes. Only `required` judges an empty value (none, an
 * empty text or list, a file cell without files); the other kinds of one column pass it. A list (several
 * options) holds each of its items against `oneOf` and `pattern`; a number
 * text counts as its number for `range`.
 *
 * Rules across rows read the other rows over `port`, at most one read per
 * rule and group: `unique` and `groupCount`/`groupSum` count the stored rows
 * of each group a row of the write enters or changes in (a row that leaves a
 * group only lowers it), `transition` allows the named changes of a value,
 * `linked` holds for the rows a row links, checked from both sides: when the
 * linking row changes, and when the write changes or deletes linked rows.
 * Empty values tell no `unique` key. A deletion breaks only `linked` rules of
 * the rows linking it.
 *
 * A rule with `when` holds only for rows whose cells after the write meet it
 * (`unique` also counts only stored rows meeting it). A violation carries the
 * rule's own `message`, when it names one.
 *
 * Self-contained: the contract spec runs it from its source alone.
 */
async function evaluateAppRules(ruleSet, change, port) {
    const linkedMax = 500;
    const violations = [];
    const empty = (value) => value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        // A file cell keeps its files by id: none left is empty.
        (typeof value === 'object' && Object.keys(value).length === 0);
    const items = (value) => Array.isArray(value) ? value : [value];
    const own = (cells, columnId) => !!cells && Object.prototype.hasOwnProperty.call(cells, columnId);
    const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
    // A value the write adds or changes.
    const written = (row, columnId) => own(row.after, columnId) &&
        (!row.before || !same(row.before[columnId], row.after[columnId]));
    const ids = (value) => Array.isArray(value)
        ? [...new Set(value.filter((id) => typeof id === 'string'))]
        : [];
    const equal = (columnId, value) => empty(value)
        ? {
            type: 'filter',
            left: { type: 'ref', name: columnId },
            function: 'isEmpty',
            args: [],
        }
        : {
            type: 'filter',
            left: { type: 'ref', name: columnId },
            function: 'is',
            args: [{ type: 'literal', value }],
        };
    const all = (...conditions) => ({
        type: 'group',
        op: 'and',
        conditions: conditions.filter((condition) => condition !== undefined),
    });
    const writeIds = change.rows.map(row => row.rowId);
    const present = change.rows.filter(row => row.after !== null);
    for (let ruleIndex = 0; ruleIndex < ruleSet.rules.length; ruleIndex++) {
        const rule = ruleSet.rules[ruleIndex];
        const broken = (rowId, columnId, code) => violations.push({
            ruleIndex,
            rowId,
            columnId,
            code,
            ...(rule.message ? { message: rule.message } : {}),
        });
        // The rule holds for the rows whose cells meet its `when`.
        const applies = async (cells) => !!cells &&
            (!('when' in rule) ||
                !rule.when ||
                (await port.matches(rule.sourceKey, cells, rule.when)));
        // A stored row the write brings under `when` answers to the rule as a
        // whole, as a new row does: its columns count as written.
        const enters = async (row) => 'when' in rule &&
            !!rule.when &&
            !!row.before &&
            !(await applies(row.before));
        if (rule.kind === 'required' ||
            rule.kind === 'range' ||
            rule.kind === 'pattern' ||
            rule.kind === 'oneOf') {
            if (rule.sourceKey !== change.sourceKey)
                continue;
            for (const row of present) {
                if (!(await applies(row.after)) ||
                    !(written(row, rule.columnId) || (await enters(row))))
                    continue;
                const value = row.after[rule.columnId];
                const fail = (code) => broken(row.rowId, rule.columnId, code);
                if (rule.kind === 'required') {
                    if (empty(value))
                        fail('APP_RULE_REQUIRED');
                    continue;
                }
                if (empty(value))
                    continue;
                if (rule.kind === 'range') {
                    const number = typeof value === 'number'
                        ? value
                        : typeof value === 'string' && value.trim() !== ''
                            ? Number(value)
                            : Number.NaN;
                    if (!Number.isFinite(number) ||
                        (rule.min !== undefined && number < rule.min) ||
                        (rule.max !== undefined && number > rule.max))
                        fail('APP_RULE_RANGE');
                    continue;
                }
                if (rule.kind === 'pattern') {
                    const pattern = new RegExp(rule.pattern, 'u');
                    if (!items(value).every(item => (typeof item === 'string' || typeof item === 'number') &&
                        pattern.test(String(item))))
                        fail('APP_RULE_PATTERN');
                    continue;
                }
                if (!items(value).every(item => typeof item === 'string' && rule.values.includes(item)))
                    fail('APP_RULE_ONE_OF');
            }
            continue;
        }
        if (rule.kind === 'transition') {
            if (rule.sourceKey !== change.sourceKey)
                continue;
            for (const row of present) {
                if (!written(row, rule.columnId) || !(await applies(row.after)))
                    continue;
                const from = String(row.before?.[rule.columnId] ?? '');
                const to = String(row.after[rule.columnId] ?? '');
                // A new row that leaves the column empty changes no value.
                if (from === to)
                    continue;
                if (!rule.allowed.some(allowed => allowed.from === from && allowed.to.includes(to)))
                    broken(row.rowId, rule.columnId, 'APP_RULE_TRANSITION');
            }
            continue;
        }
        if (rule.kind === 'unique') {
            if (rule.sourceKey !== change.sourceKey)
                continue;
            const keyed = [];
            for (const row of present)
                if (rule.columnIds.every(columnId => !empty(row.after[columnId])) &&
                    (await applies(row.after)))
                    keyed.push(row);
            const byKey = new Map();
            for (const row of keyed) {
                const key = JSON.stringify(rule.columnIds.map(columnId => row.after[columnId]));
                byKey.set(key, [...(byKey.get(key) ?? []), row]);
            }
            for (const rows of byKey.values()) {
                const changed = [];
                for (const row of rows)
                    if (rule.columnIds.some(columnId => written(row, columnId)) ||
                        (await enters(row)))
                        changed.push(row);
                if (changed.length === 0)
                    continue;
                const stored = rows.length > 1
                    ? 0
                    : (await port.countRows(rule.sourceKey, {
                        where: all(...(rule.when ? [rule.when] : []), ...rule.columnIds.map(columnId => equal(columnId, rows[0].after[columnId]))),
                        excludeRowIds: writeIds,
                    })).count;
                if (rows.length + stored > 1)
                    for (const row of changed)
                        broken(row.rowId, rule.columnIds[0], 'APP_RULE_UNIQUE');
            }
            continue;
        }
        if (rule.kind === 'groupCount' || rule.kind === 'groupSum') {
            if (rule.sourceKey !== change.sourceKey)
                continue;
            const inside = async (cells) => !!cells &&
                (!rule.where ||
                    (await port.matches(rule.sourceKey, cells, rule.where)));
            const groupKey = (cells) => JSON.stringify(rule.groupBy.map(columnId => cells[columnId] ?? null));
            const groups = new Map();
            for (const row of present) {
                if (!(await inside(row.after)))
                    continue;
                const key = groupKey(row.after);
                groups.set(key, [...(groups.get(key) ?? []), row]);
            }
            const sumColumnId = rule.kind === 'groupSum' ? rule.columnId : undefined;
            const amount = (cells) => {
                const value = sumColumnId === undefined ? 1 : cells[sumColumnId];
                const number = typeof value === 'number'
                    ? value
                    : typeof value === 'string' && value.trim() !== ''
                        ? Number(value)
                        : 0;
                return Number.isFinite(number) ? number : 0;
            };
            for (const rows of groups.values()) {
                // Rows that enter the group or, for a sum, change their amount.
                const raising = [];
                for (const row of rows)
                    if (!row.before ||
                        !(await inside(row.before)) ||
                        groupKey(row.before) !== groupKey(row.after) ||
                        (sumColumnId !== undefined && written(row, sumColumnId)))
                        raising.push(row);
                if (raising.length === 0)
                    continue;
                const stored = await port.countRows(rule.sourceKey, {
                    where: all(...(rule.where ? [rule.where] : []), ...rule.groupBy.map(columnId => equal(columnId, rows[0].after[columnId]))),
                    excludeRowIds: writeIds,
                    ...(sumColumnId !== undefined ? { sumColumnId } : {}),
                });
                const total = (sumColumnId === undefined ? stored.count : stored.sum) +
                    rows.reduce((sum, row) => sum + amount(row.after), 0);
                if (total > rule.max)
                    for (const row of raising)
                        broken(row.rowId, rule.kind === 'groupSum'
                            ? rule.columnId
                            : (rule.groupBy[0] ?? '@row'), rule.kind === 'groupSum'
                            ? 'APP_RULE_GROUP_SUM'
                            : 'APP_RULE_GROUP_COUNT');
            }
            continue;
        }
        // linked: the rows a row links meet the filter: all, some or none.
        const targetWrite = new Map(rule.targetSourceKey === change.sourceKey
            ? change.rows.map(row => [row.rowId, row.after])
            : []);
        // The linked rows that exist after the write and those meeting the filter.
        const judge = async (linkedIds) => {
            const kept = linkedIds.filter(id => targetWrite.get(id) !== null);
            const stored = kept.filter(id => !targetWrite.has(id));
            const meeting = new Set(stored.length
                ? await port.matchingRowIds(rule.targetSourceKey, {
                    where: rule.filter,
                    rowIds: stored,
                })
                : []);
            for (const id of kept)
                if (targetWrite.has(id) &&
                    (await port.matches(rule.targetSourceKey, targetWrite.get(id), rule.filter)))
                    meeting.add(id);
            return { kept, meeting };
        };
        const holds = (kept, meeting) => {
            const hits = kept.filter(id => meeting.has(id)).length;
            return rule.quantifier === 'all'
                ? hits === kept.length
                : rule.quantifier === 'some'
                    ? hits > 0
                    : hits === 0;
        };
        if (rule.sourceKey === change.sourceKey) {
            const linking = [];
            for (const row of present)
                if ((await applies(row.after)) &&
                    (written(row, rule.columnId) || !(await applies(row.before))))
                    linking.push(row);
            if (linking.length > 0) {
                const { kept, meeting } = await judge(linking.flatMap(row => ids(row.after[rule.columnId])));
                const keptIds = new Set(kept);
                for (const row of linking) {
                    const linked = ids(row.after[rule.columnId]).filter(id => keptIds.has(id));
                    if (!holds(linked, meeting))
                        broken(row.rowId, rule.columnId, 'APP_RULE_LINKED');
                }
            }
        }
        if (rule.targetSourceKey === change.sourceKey) {
            const found = await port.linkingRows(rule.sourceKey, {
                columnId: rule.columnId,
                targetRowIds: writeIds,
                ...(rule.when ? { when: rule.when } : {}),
                limit: linkedMax + 1,
            });
            // Rows the write itself changes were judged above.
            const others = rule.sourceKey === change.sourceKey
                ? found.filter(row => !writeIds.includes(row.rowId))
                : found;
            if (others.length > linkedMax) {
                broken(writeIds[0], rule.columnId, 'APP_RULE_LINKED_LIMIT');
                continue;
            }
            if (others.length > 0) {
                const { kept, meeting } = await judge(others.flatMap(row => ids(row.values[rule.columnId])));
                const keptIds = new Set(kept);
                for (const row of others) {
                    const linked = ids(row.values[rule.columnId]).filter(id => keptIds.has(id));
                    if (!holds(linked, meeting))
                        broken(row.rowId, rule.columnId, 'APP_RULE_LINKED');
                }
            }
        }
    }
    return violations;
}
